import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  SCENE_MENU,
  SCENE_MENU_CHAPTER,
  SCENE_MENU_CHAT,
  SCENE_MENU_DELETE,
  SCENE_MENU_DELETE_BODY,
  SCENE_MENU_DELETE_CONFIRM,
  SCENE_MENU_DELETE_TITLE,
  SCENE_MENU_KEEP,
  SCENE_MENU_MOVE,
  SCENE_EDIT_PLACE_AFTER,
  SCENE_EDIT_PLACE_START_OF,
  SCENE_MENU_OPEN,
  SCENE_MENU_RENAME,
  SCENE_MENU_RENAME_SAVE,
} from '../../utils/writerCopy';
import { clearPlotTrackDraft, readPlotTrackDraft, writePlotTrackDraft } from './plotTrackDraft';

/**
 * Direct actions on one Outline scene. Nothing here goes through chat
 * except the one item that opens the chat pane on this scene.
 */
export function moveSpots(sequence, sceneId, titles = {}) {
  const ids = (sequence?.scene_ids || []).filter((id) => String(id) !== String(sceneId));
  const spots = [{
    key: `start:${sequence.id}`,
    label: SCENE_EDIT_PLACE_START_OF(sequence.display_name),
    place: { sequenceId: sequence.id, atStart: true },
  }];
  ids.forEach((id) => {
    spots.push({
      key: `after:${sequence.id}:${id}`,
      label: SCENE_EDIT_PLACE_AFTER(titles[String(id)] || 'Untitled scene'),
      place: { sequenceId: sequence.id, afterSceneId: id, atStart: false },
    });
  });
  return spots;
}

export default function SceneCardMenu({
  sceneName,
  sceneId = null,
  sequences = [],
  titles = {},
  canStartChapter = false,
  offerStartChapter = true,
  onOpen,
  onRename,
  onMove,
  onStartChapter,
  onChat,
  onDelete,
}) {
  const renameKey = sceneId ? `rename:${sceneId}` : '';
  const savedMenu = readPlotTrackDraft(renameKey);
  const [open, setOpen] = useState(Boolean(savedMenu?.open));
  const [moveOpen, setMoveOpen] = useState(false);
  const [renaming, setRenaming] = useState(Boolean(savedMenu?.renaming));
  const [name, setName] = useState(savedMenu?.name || sceneName || '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [place, setPlace] = useState(null);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const confirmRef = useRef(null);
  const nameRef = useRef(name);
  const renamingRef = useRef(renaming);
  const committedRef = useRef(false);
  const leavingRef = useRef(false);
  const commitRef = useRef(() => {});
  nameRef.current = name;
  renamingRef.current = renaming;

  useEffect(() => {
    if (!renameKey) return undefined;
    if (!open) {
      clearPlotTrackDraft(renameKey);
      return undefined;
    }
    writePlotTrackDraft(renameKey, { open: true, renaming, name });
    return undefined;
  }, [renameKey, open, renaming, name]);

  useLayoutEffect(() => {
    leavingRef.current = false;
    return () => { leavingRef.current = true; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const target = event.target;
      if (
        ref.current?.contains(target)
        || panelRef.current?.contains(target)
        || confirmRef.current?.contains(target)
      ) return;
      if (renamingRef.current) commitRef.current(nameRef.current);
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return undefined;
    }
    const placePanel = () => {
      const rect = ref.current?.querySelector('button')?.getBoundingClientRect();
      const panel = panelRef.current;
      if (!rect || !panel) return;
      const card = ref.current?.closest('.pt-scene-card')?.getBoundingClientRect();
      const anchor = card && card.width >= 80 ? card : rect;
      const margin = 8;
      const gap = 4;
      // Stay on this card. A 220px menu aligned to the button covered the card beside it.
      const onCard = Boolean(card && card.width >= 80);
      let width = onCard ? Math.min(220, anchor.width) : 220;
      let left = anchor.right - width;
      if (left < anchor.left) left = anchor.left;
      if (left < margin) {
        left = margin;
        width = Math.min(width, window.innerWidth - margin * 2);
      }
      if (left + width > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - width - margin);
      }
      const height = panel.offsetHeight || 0;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const openBelow = height === 0 || spaceBelow >= height || spaceBelow >= spaceAbove;
      let top = openBelow ? rect.bottom + gap : rect.top - gap - height;
      const maxTop = window.innerHeight - margin - Math.min(height || 0, window.innerHeight - margin * 2);
      if (top > maxTop) top = Math.max(margin, maxTop);
      if (top < margin) top = margin;
      setPlace({ left, top, width });
    };
    placePanel();
    // Following scroll while the name field is focused moves the panel
    // out from under the caret and the menu closes after one letter.
    if (renaming) return undefined;
    window.addEventListener('resize', placePanel);
    window.addEventListener('scroll', placePanel, true);
    return () => {
      window.removeEventListener('resize', placePanel);
      window.removeEventListener('scroll', placePanel, true);
    };
  }, [open, renaming]);

  const close = () => {
    setOpen(false);
    setMoveOpen(false);
    setRenaming(false);
    setConfirmDelete(false);
  };

  const commitRename = (raw) => {
    if (committedRef.current || leavingRef.current) return;
    const next = String(raw ?? nameRef.current ?? '').trim();
    if (!next || !onRename || next === String(sceneName || '').trim()) {
      setRenaming(false);
      return;
    }
    committedRef.current = true;
    close();
    onRename(next);
  };
  commitRef.current = commitRename;

  const parts = (sequences || []).filter((sequence) => sequence?.id && sequence?.display_name);

  return (
    <div className="pt-scene-menu" ref={ref}>
      <button
        type="button"
        className="pt-scene-menu__btn"
        aria-label={SCENE_MENU(sceneName || 'this scene')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setName(sceneName || '');
          setOpen((value) => !value);
        }}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && createPortal(
        <div
          className="pt-scene-menu__panel pt-scene-menu__panel--portal"
          role="menu"
          aria-label={SCENE_MENU(sceneName || 'this scene')}
          ref={panelRef}
          style={{
            position: 'fixed',
            zIndex: 200,
            left: place?.left ?? 0,
            top: place?.top ?? 0,
            width: place?.width ? `${place.width}px` : undefined,
            minWidth: place?.width ? `${place.width}px` : undefined,
            bottom: 'auto',
            right: 'auto',
            visibility: place ? 'visible' : 'hidden',
          }}
        >
          <button type="button" role="menuitem" className="pt-scene-menu__item" onClick={() => { close(); onOpen?.(); }}>
            {SCENE_MENU_OPEN}
          </button>
          {renaming ? (
            <form
              className="pt-scene-menu__rename"
              onSubmit={(event) => {
                event.preventDefault();
                commitRename(name);
              }}
            >
              <label>
                {SCENE_MENU_RENAME}
                <input
                  type="text"
                  value={name}
                  autoFocus
                  onChange={(event) => setName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Escape') event.stopPropagation();
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitRename(event.currentTarget.value);
                    }
                  }}
                  onBlur={(event) => {
                    if (leavingRef.current) return;
                    const nextFocus = event.relatedTarget;
                    if (nextFocus && event.currentTarget.form?.contains(nextFocus)) return;
                    commitRename(event.currentTarget.value);
                  }}
                />
              </label>
              <button type="submit">{SCENE_MENU_RENAME_SAVE}</button>
            </form>
          ) : (
            <button
              type="button"
              role="menuitem"
              className="pt-scene-menu__item"
              disabled={!onRename}
              onClick={() => {
                if (!onRename) return;
                committedRef.current = false;
                setRenaming(true);
              }}
            >
              {SCENE_MENU_RENAME}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="pt-scene-menu__item"
            aria-expanded={moveOpen}
            disabled={!onMove || parts.length === 0}
            onClick={() => { if (onMove && parts.length) setMoveOpen((value) => !value); }}
          >
            {SCENE_MENU_MOVE}
          </button>
          {moveOpen && (
            <div className="pt-scene-menu__sub" role="group" aria-label={SCENE_MENU_MOVE}>
              {parts.map((sequence) => (
                <div
                  key={sequence.id}
                  className="pt-scene-menu__group"
                  role="group"
                  aria-label={sequence.display_name}
                >
                  <div className="pt-scene-menu__group-title" aria-hidden="true">
                    {sequence.display_name}
                  </div>
                  {moveSpots(sequence, sceneId, titles).map((spot) => (
                    <button
                      key={spot.key}
                      type="button"
                      className="pt-scene-menu__item"
                      onClick={() => { close(); onMove(spot.place); }}
                    >
                      {spot.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
          {offerStartChapter && (
            <button
              type="button"
              role="menuitem"
              className="pt-scene-menu__item"
              disabled={!canStartChapter || !onStartChapter}
              onClick={() => {
                if (!canStartChapter || !onStartChapter) return;
                close();
                onStartChapter();
              }}
            >
              {SCENE_MENU_CHAPTER}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="pt-scene-menu__item"
            disabled={!onChat}
            onClick={() => { if (!onChat) return; close(); onChat(); }}
          >
            {SCENE_MENU_CHAT}
          </button>
          <button
            type="button"
            role="menuitem"
            className="pt-scene-menu__item pt-scene-menu__item--danger"
            disabled={!onDelete}
            onClick={() => { if (onDelete) setConfirmDelete(true); }}
          >
            {SCENE_MENU_DELETE}
          </button>
        </div>,
        document.body,
      )}
      {open && confirmDelete && createPortal(
        <div
          className="pt-scene-menu__confirm pt-scene-menu__confirm--layer"
          role="dialog"
          aria-labelledby="pt-scene-delete-title"
          ref={confirmRef}
        >
          <h3 id="pt-scene-delete-title">{SCENE_MENU_DELETE_TITLE}</h3>
          <p>{SCENE_MENU_DELETE_BODY}</p>
          <button type="button" onClick={() => setConfirmDelete(false)}>{SCENE_MENU_KEEP}</button>
          <button
            type="button"
            onClick={() => { close(); onDelete(); }}
          >
            {SCENE_MENU_DELETE_CONFIRM}
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}
